import { useCallback } from 'react'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import { Divider, IconButton, Text, useTheme } from 'react-native-paper'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import { displayLocationName } from '@/lib/location-utils'
import { useSelectedLocation } from '@/stores/monitor-store'
import type { UserGeofenceActivity } from '@/types/movement'
import { LocationActivityTable } from './LocationActivityTable'

interface LocationActivityDrawerProps {
    isVisible: boolean
    location: LocationCircle | null
    activity: UserGeofenceActivity[]
    isPending: boolean
    userColorMap?: Map<string, string>
}

export function LocationActivityDrawer({
    isVisible,
    location,
    activity,
    isPending,
    userColorMap,
}: LocationActivityDrawerProps) {
    const theme = useTheme()
    const { setSelectedLocation } = useSelectedLocation()

    const handleClose = useCallback(() => {
        setSelectedLocation(null)
    }, [setSelectedLocation])

    if (!isVisible || !location) return null

    return (
        <>
            <Pressable style={styles.backdrop} onPress={handleClose} />
            <View
                style={[
                    styles.drawer,
                    {
                        backgroundColor: theme.colors.surface,
                        borderTopColor: theme.colors.outline,
                    },
                ]}
            >
                <View style={[styles.handle, { backgroundColor: theme.colors.outline }]} />
                <View style={styles.header}>
                    <View style={styles.titleContainer}>
                        <Text variant="titleMedium" style={{ color: theme.colors.onSurface }}>
                            {displayLocationName(location.name) ?? location.jobName}
                        </Text>
                        {displayLocationName(location.name) ? (
                            <Text
                                variant="bodySmall"
                                style={{ color: theme.colors.onSurfaceVariant }}
                            >
                                {location.jobName}
                            </Text>
                        ) : null}
                    </View>
                    <IconButton icon="close" onPress={handleClose} size={20} />
                </View>
                <Divider />
                <ScrollView style={styles.scrollArea}>
                    <LocationActivityTable
                        activity={activity}
                        isPending={isPending}
                        userColorMap={userColorMap}
                    />
                </ScrollView>
            </View>
        </>
    )
}

const styles = StyleSheet.create({
    backdrop: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 10,
    },
    drawer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        maxHeight: 300,
        borderTopWidth: 1,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        paddingHorizontal: 16,
        paddingBottom: 24,
        zIndex: 11,
        elevation: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
    },
    handle: {
        width: 32,
        height: 4,
        borderRadius: 2,
        alignSelf: 'center',
        marginTop: 8,
        marginBottom: 8,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: 8,
    },
    titleContainer: {
        flex: 1,
    },
    scrollArea: {
        maxHeight: 200,
    },
})
